import React, { useState, useRef, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Search, Users, Eye } from "lucide-react";
import { API_URL, getAuthHeader } from "@/config";
import { Filter as StudentFilter } from "../FormationFilterEditor";

interface Student {
    email: string;
    first_name: string;
    last_name: string;
}

interface FormationStudentPreviewProps {
    formationId: number;
    filters: StudentFilter[];
    excludedEmails: string[];
    onExcludedChange: (emails: string[]) => void;
    token: string | null;
}

const FormationStudentPreview: React.FC<FormationStudentPreviewProps> = ({
    formationId,
    filters,
    excludedEmails,
    onExcludedChange,
    token,
}) => {
    const [students, setStudents] = useState<Student[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    // Track which filters were used for the last fetch to invalidate cache
    const lastFiltersRef = useRef<string>("");

    const fetchStudents = useCallback(async () => {
        if (!token) return;

        const filtersKey = JSON.stringify(filters);

        // If already loaded with same filters, don't refetch
        if (isLoaded && filtersKey === lastFiltersRef.current) return;

        setIsLoading(true);
        try {
            const response = await fetch(`${API_URL}/virtual-classes/preview-students`, {
                method: "POST",
                headers: getAuthHeader(token),
                body: JSON.stringify({
                    formation_id: formationId,
                    filters: filters,
                }),
            });
            if (response.ok) {
                const data = await response.json();
                setStudents(data.students || []);
                setIsLoaded(true);
                lastFiltersRef.current = filtersKey;
            }
        } catch (error) {
            console.error("Failed to fetch students for formation", formationId, error);
        } finally {
            setIsLoading(false);
        }
    }, [token, formationId, filters, isLoaded]);

    // Invalidate cache when filters change
    useEffect(() => {
        const currentFiltersKey = JSON.stringify(filters);
        if (isLoaded && currentFiltersKey !== lastFiltersRef.current) {
            setIsLoaded(false);
            setStudents([]);
        }
    }, [filters, isLoaded]);

    const toggleStudent = (email: string) => {
        const lower = email.toLowerCase();
        if (excludedEmails.includes(lower)) {
            onExcludedChange(excludedEmails.filter(e => e !== lower));
        } else {
            onExcludedChange([...excludedEmails, lower]);
        }
    };

    const filteredStudents = students.filter(s => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return (
            s.email.toLowerCase().includes(q) ||
            (s.first_name || "").toLowerCase().includes(q) ||
            (s.last_name || "").toLowerCase().includes(q)
        );
    });

    const selectedCount = students.filter(s => !excludedEmails.includes(s.email.toLowerCase())).length;

    if (!isLoaded) {
        return (
            <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-[10px] font-bold text-primary/70 hover:text-primary h-7 px-2 rounded-lg"
                onClick={fetchStudents}
                disabled={isLoading}
            >
                {isLoading ? (
                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                ) : (
                    <Eye className="h-3 w-3 mr-1" />
                )}
                Voir les étudiants
            </Button>
        );
    }

    return (
        <div className="mt-2 ml-8 border border-slate-100 rounded-xl bg-white p-3 space-y-2 animate-in fade-in slide-in-from-top-2 duration-300">
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-[10px] font-bold text-slate-500">
                    <Users className="h-3 w-3" />
                    {selectedCount} / {students.length} étudiants sélectionnés
                </div>
                <div className="relative w-40">
                    <Search className="absolute left-2 top-1.5 h-3 w-3 text-slate-400" />
                    <Input
                        placeholder="Rechercher..."
                        className="h-6 pl-7 text-[10px] bg-slate-50 border-slate-100 rounded-md"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
            </div>
            <div className="max-h-40 overflow-y-auto space-y-0.5">
                {filteredStudents.map(student => {
                    const isExcluded = excludedEmails.includes(student.email.toLowerCase());
                    return (
                        <label
                            key={student.email}
                            className="flex items-center gap-2 py-1 px-2 rounded-md hover:bg-slate-50 cursor-pointer group"
                        >
                            <Checkbox
                                checked={!isExcluded}
                                onCheckedChange={() => toggleStudent(student.email)}
                                className="h-3.5 w-3.5 rounded border-slate-300 data-[state=checked]:bg-primary"
                            />
                            <span className={`text-[10px] font-medium truncate flex-1 ${isExcluded ? 'text-slate-300 line-through' : 'text-slate-600'}`}>
                                {student.first_name} {student.last_name}
                            </span>
                            <span className={`text-[10px] truncate ${isExcluded ? 'text-slate-300' : 'text-slate-400'}`}>
                                {student.email}
                            </span>
                        </label>
                    );
                })}
                {filteredStudents.length === 0 && (
                    <p className="text-[10px] text-slate-400 italic text-center py-2">
                        {searchQuery ? "Aucun résultat" : "Aucun étudiant trouvé"}
                    </p>
                )}
            </div>
        </div>
    );
};

export default FormationStudentPreview;
